import Textarea from "../../../../../../ui/Textarea/Textarea";
import Title from "./Title";
import {
  ChevronDown,
  ChevronRight,
  DragHandle,
} from "../../../../../../ui/Icon/Icon";
import IconButton from "../../../../../../ui/IconButton/IconButton";
import { useEffect, useRef } from "react";
import styles from "./OutlineSection.module.scss";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { useMergeRefs } from "@floating-ui/react";
import classNames from "classnames";

function OutlineSection({
  index,
  titleRef,
  setTitleRef,
  descriptionRef,
  setDescriptionRef,
  value,
  onChange,
  appendSection,
  deleteSection,
  focusNext,
  focusPrevious,
  disabled,
}: {
  index: number;
  titleRef: HTMLInputElement | null;
  setTitleRef: (ref: HTMLInputElement | null) => void;
  descriptionRef: HTMLTextAreaElement | null;
  setDescriptionRef: (ref: HTMLTextAreaElement) => void;
  value: {
    id?: string;
    internalId: string;
    title?: string;
    titleDisabled?: boolean;
    description?: string;
    isExpanded: boolean;
  };
  onChange: (
    value: {
      title?: string;
      description?: string;
      isExpanded?: boolean;
    },
    replace?: boolean,
  ) => void;
  appendSection: () => void;
  deleteSection: () => void;
  focusNext?: () => void;
  focusPrevious?: () => void;
  disabled?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value.internalId, disabled });
  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        zIndex: isDragging ? 1 : undefined,
      }
    : undefined;

  const setIsExpanded = (isExpanded: boolean) => {
    onChange(
      {
        title: value.title,
        description: value.description,
        isExpanded,
      },
      true,
    );
  };

  const ref = useRef<HTMLTextAreaElement>(null);
  const previousValue = useRef<string | undefined>(value.description);
  const cursor = useRef<number | null>(null);
  const changeFromInputRef = useRef(false);

  const refs = useMergeRefs<HTMLTextAreaElement>([ref, setDescriptionRef]);

  useEffect(() => {
    const input = ref.current;

    if (!changeFromInputRef.current && cursor.current && input) {
      let newCursor = cursor.current;

      if (value.description && previousValue.current) {
        if (value.description.length < previousValue.current.length) {
          const indexOfChange = [...previousValue.current].findIndex(
            (el, index) => el !== value.description?.[index],
          );

          if (indexOfChange < newCursor) {
            newCursor -=
              previousValue.current.length - value.description.length;
          }
        } else {
          const indexOfChange = [...value.description].findIndex(
            (el, index) => el !== previousValue.current?.[index],
          );

          if (indexOfChange < newCursor) {
            newCursor +=
              value.description.length - previousValue.current.length;
          }
        }
      }

      input.setSelectionRange(newCursor, newCursor);
      cursor.current = newCursor;
    }

    changeFromInputRef.current = false;
    previousValue.current = value.description;
  }, [cursor, value.description]);

  return (
    <div className={styles.section} ref={setNodeRef} style={style}>
      <button
        type="button"
        draggable="true"
        data-drag-handle
        className={classNames(styles.dragHandle, {
          [styles.dragHandleDisabled]: disabled,
          [styles.dragging]: isDragging,
        })}
        {...listeners}
        {...attributes}
        tabIndex={-1}
      >
        <DragHandle size="medium" />
      </button>
      <div className={styles.sectionContent}>
        <div className={styles.sectionTitle}>
          <Title
            index={index}
            disabled={disabled}
            titleDisabled={value.titleDisabled}
            titleRef={setTitleRef}
            headingLevel={2}
            value={value.title}
            onChange={(v) =>
              onChange({ title: v, description: value.description })
            }
            appendSection={appendSection}
            deleteSection={() => {
              deleteSection();
              focusPrevious?.();
            }}
            focusNext={() => {
              if (!value.isExpanded) {
                focusNext?.();
              } else {
                if (descriptionRef) {
                  descriptionRef.focus();

                  setTimeout(() => {
                    if (titleRef) {
                      descriptionRef.setSelectionRange(
                        titleRef.selectionStart,
                        titleRef.selectionStart,
                      );
                    }
                  });
                }
              }
            }}
            focusPrevious={focusPrevious}
          />
          <IconButton
            className={styles.icon}
            aria-label={value.isExpanded ? "Collapse" : "Expand"}
            IconComponent={value.isExpanded ? ChevronDown : ChevronRight}
            iconSize="small"
            onClick={() => {
              setIsExpanded(!value.isExpanded);
            }}
          />
        </div>
        {value.isExpanded && (
          <Textarea
            disabled={disabled}
            ref={refs}
            value={value.description}
            placeholder="Write section description"
            autosize
            unstyled
            minRows={1}
            className={styles.description}
            onChange={(event) => {
              changeFromInputRef.current = true;
              cursor.current = event.target.selectionStart;
              onChange({ title: value.title, description: event.target.value });
            }}
            onFocus={(event) => {
              cursor.current = event.target.selectionStart;
            }}
            onBlur={() => {
              cursor.current = null;
            }}
            onMouseUp={(event) => {
              cursor.current = event.currentTarget.selectionStart;
            }}
            onKeyDown={(event) => {
              cursor.current = ref.current?.selectionStart ?? null;

              // Arrow Down
              if (event.key === "ArrowDown") {
                if (
                  event.currentTarget.selectionStart ===
                  event.currentTarget.value.length
                ) {
                  focusNext?.();
                }
              }

              // Arrow Up
              if (event.key === "ArrowUp") {
                if (event.currentTarget.selectionStart === 0) {
                  if (titleRef) {
                    titleRef.focus();

                    setTimeout(() => {
                      if (descriptionRef) {
                        titleRef.setSelectionRange(
                          descriptionRef.selectionStart,
                          descriptionRef.selectionStart,
                        );
                      }
                    });
                  }
                }
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

export default OutlineSection;
